import React from "react";
import dayjs from "dayjs";
import { GET_SERVICE_TIME } from "./graphql";
import { Card, Kpi, ReportState, SortTable, PersonButton, BookingLink, useReport, fmt, mins } from "./ui";

// Real Start-to-Finish time against the minutes the booked services take.

const pctOf = (a, b) => (b ? Math.round((a / b) * 100) : null);
const Ratio = ({ v }) => {
  if (v === null || v === undefined) return <span className="text-gray-300">—</span>;
  const cls = v < 0.5 ? "text-red-600 font-medium" : v < 0.8 ? "text-amber-600" : v > 1.5 ? "text-violet-700" : "text-gray-900";
  return <span className={cls}>{Math.round(v * 100)}%</span>;
};

const ServiceTimeTab = ({ filter, onPick, onOpenBooking }) => {
  const { data, loading, error } = useReport(GET_SERVICE_TIME, "getServiceTimeReport", filter);
  const t = data?.team || {};

  const empColumns = [
    { key: "measured", label: "Measured", title: "Completed bookings with Start and Finish in the app", cell: (e) => `${fmt(e.measured)}/${fmt(e.completed)}` },
    { key: "medianRatio", label: "Real vs booked", title: "Median of real ÷ booked minutes (100% = exactly as booked)", cell: (e) => <Ratio v={e.medianRatio} /> },
    { key: "medianActual", label: "Median real", cell: (e) => mins(e.medianActual) },
    { key: "medianExpected", label: "Median booked", cell: (e) => mins(e.medianExpected) },
    {
      key: "shortShare",
      label: "Too short",
      title: "Took under half the booked time",
      cell: (e) => (e.tooShort ? <span className="text-red-600">{e.tooShort} <span className="text-xs text-gray-400">({pctOf(e.tooShort, e.measured)}%)</span></span> : <span className="text-gray-300">0</span>),
      sort: (e) => (e.measured ? e.tooShort / e.measured : 0),
    },
    {
      key: "longShare",
      label: "Too long",
      title: "Took over 1.5× the booked time",
      cell: (e) => (e.tooLong ? <span className="text-violet-700">{e.tooLong} <span className="text-xs text-gray-400">({pctOf(e.tooLong, e.measured)}%)</span></span> : <span className="text-gray-300">0</span>),
      sort: (e) => (e.measured ? e.tooLong / e.measured : 0),
    },
    { key: "minutesVsBooked", label: "Total ± min", title: "Real minus booked, summed: negative = less time than customers paid for", cell: (e) => <span className={e.minutesVsBooked < 0 ? "text-red-600" : "text-gray-700"}>{fmt(e.minutesVsBooked)}</span> },
  ];

  const svcColumns = [
    { key: "bookings", label: "Bookings" },
    { key: "bookedMinutes", label: "Booked", cell: (s) => mins(s.bookedMinutes) },
    { key: "medianActual", label: "Median real", cell: (s) => mins(s.medianActual) },
    { key: "range", label: "Middle half", title: "25th to 75th percentile of real minutes", cell: (s) => `${Math.round(s.p25)}–${Math.round(s.p75)} min`, sort: (s) => s.p75 - s.p25 },
    { key: "ratio", label: "Real vs booked", cell: (s) => <Ratio v={s.bookedMinutes ? s.medianActual / s.bookedMinutes : null} />, sort: (s) => (s.bookedMinutes ? s.medianActual / s.bookedMinutes : 0) },
    { key: "shortShare", label: "Too short", cell: (s) => (s.shortShare ? <span className="text-red-600">{s.shortShare}%</span> : <span className="text-gray-300">0%</span>) },
  ];

  const shortColumns = [
    { key: "bookingDate", label: "Date", cell: (r) => `${dayjs(r.bookingDate).format("D MMM")} ${r.timeSlot || ""}`, sort: (r) => r.bookingDate },
    { key: "bookingId", label: "Booking", cell: (r) => <BookingLink row={r} onOpen={onOpenBooking} />, sort: (r) => r.bookingId },
    { key: "name", label: "Professional", cell: (r) => <PersonButton row={r} onPick={onPick} />, sort: (r) => r.name || "" },
    { key: "services", label: "Services", cell: (r) => <span className="text-gray-600 inline-block max-w-xs truncate align-bottom" title={r.services}>{r.services}</span>, sort: (r) => r.services || "" },
    { key: "actual", label: "Real", cell: (r) => mins(r.actual) },
    { key: "expected", label: "Booked", cell: (r) => mins(r.expected) },
    { key: "ratio", label: "%", cell: (r) => <Ratio v={r.ratio} /> },
  ];

  return (
    <ReportState loading={loading} error={error} data={data}>
      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi label="Real vs booked" value={t.medianRatio ? `${Math.round(t.medianRatio * 100)}%` : "—"} sub={`median of ${fmt(t.measured)} timed bookings`} />
            <Kpi label="Median service" value={mins(t.medianActual)} sub={`booked ${mins(t.medianExpected)}`} />
            <Kpi label="Too short" value={fmt(t.tooShort)} valueClass={t.tooShort ? "text-red-600" : "text-gray-900"} sub={`${pctOf(t.tooShort, t.measured) ?? 0}% took under half the booked time`} />
            <Kpi label="Too long" value={fmt(t.tooLong)} valueClass={t.tooLong ? "text-violet-700" : "text-gray-900"} sub="over 1.5× — slow, or the service time is wrong" />
          </div>

          <Card title="By professional" hint="Real time is Start to Finish in the app. Much shorter than booked usually means steps skipped or Start pressed late.">
            <SortTable
              columns={empColumns}
              rows={data.employees}
              initialSort={{ key: "shortShare", dir: -1 }}
              rowKey={(e) => e.employeeId}
              nameCell={{ label: "Professional", cell: (e) => <PersonButton row={e} onPick={onPick} /> }}
            />
          </Card>

          <Card title="By service" hint="Bookings with just this one service, at least 3. If the whole team is always faster or slower, the service's booked time may be wrong.">
            <SortTable columns={svcColumns} rows={data.services} initialSort={{ key: "bookings", dir: -1 }} rowKey={(s) => s.serviceId} nameCell={{ label: "Service", cell: (s) => <span className="font-medium text-gray-900">{s.name}</span> }} />
          </Card>

          <Card title="Much shorter than booked" hint={`Under half the booked time, shortest first${data.short.length >= 150 ? " (first 150)" : ""}.`}>
            <SortTable columns={shortColumns} rows={data.short} initialSort={{ key: "ratio", dir: 1 }} rowKey={(r) => r.orderId} />
          </Card>
        </>
      )}
    </ReportState>
  );
};

export default ServiceTimeTab;
